// src/components/PresensiCardUmatInfo.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import LookupUmat from "./LookupUmat";
import LookupExternalPerson from "./LookupExternalPerson";
import { getUser } from "../utils/auth";
import { apiFetch, getAvatarPlaceholder } from "../utils/wkCommon";

const rem = (v) => `${v}rem`;

function SummaryHeader({ avatar, umatID, chineseName, pinyinName, fullName }) {
  return (
    <div style={{ display: "flex", gap: rem(0.8), alignItems: "center", marginBottom: rem(0.6) }}>
      <div style={{ width: rem(5), flex: `0 0 ${rem(5)}` }}>
        <img
          src={avatar || getAvatarPlaceholder()}
          alt={fullName || "avatar"}
          style={{ width: rem(5), height: rem(5), borderRadius: "999px", objectFit: "cover", border: "1px solid rgba(0,0,0,0.06)" }}
        />
      </div>

      <div style={{ flex: 1 }}>
        <div style={{ fontSize: rem(0.95), fontWeight: 800, color: "#0b2f4a" }}>
          <span style={{ marginRight: rem(0.3) }}>{umatID || "-"}</span>
          {chineseName ? <span style={{ fontWeight: 600 }}>{` | ${chineseName} ${pinyinName || ""}`}</span> : null}
        </div>
        <div style={{ marginTop: rem(0.18), fontSize: rem(1.02), fontWeight: 700, color: "#0b2f4a" }}>{fullName || "-"}</div>
        <div style={{ marginTop: rem(0.18), fontSize: rem(0.85), color: "#51606a" }} />
      </div>
    </div>
  );
}

export default function PresensiCardUmatInfo({
  participant,
  onSave,
  onCancel,
  getNextSeqNo,
  selectedAgenda,
  selectedEventId,
  firstEventId,
  lookupSeqFromFirstEvent, // function(participant) => seqNo|null
}) {
  const baseParticipant = participant || {};

  const isEditingExisting = !!baseParticipant?.documentId;
  const isFromPreReg = !!baseParticipant?._isPreReg;

  const initialSelectedUmat = (() => {
    if (baseParticipant?.umats && Array.isArray(baseParticipant.umats) && baseParticipant.umats.length) return baseParticipant.umats;
    if (baseParticipant?.umat && baseParticipant.umat.id) return baseParticipant.umat;
    return null;
  })();
  const initialSelectedExternal = (() => {
    if (baseParticipant?.external_people && Array.isArray(baseParticipant.external_people) && baseParticipant.external_people.length) return baseParticipant.external_people;
    if (baseParticipant?.external_person && baseParticipant.external_person.id) return baseParticipant.external_person;
    return null;
  })();

  const [selectedUmat, setSelectedUmat] = useState(initialSelectedUmat);
  const [selectedExternalPerson, setSelectedExternalPerson] = useState(initialSelectedExternal);
  const [lockedKodeUmat, setLockedKodeUmat] = useState(null);
  const [lockedKodeExternalPerson, setLockedKodeExternalPerson] = useState(null);
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState(() => {
    const p = baseParticipant || {};
    const n = p.non_umat_pre_registration || {};
    return {
      ...p,
      umat: { ...(p.umat || {}) },
      external_person: { ...(p.external_person || {}) },
      umats: p.umats ? [...p.umats] : [],
      external_people: p.external_people ? [...p.external_people] : [],
      nonUmatName: n.nonUmatName || "",
      nonUmatPhoneNo: n.nonUmatPhoneNo || "",
      nonUmatRemarks: n.nonUmatRemarks || "",
      nonUmatGender: n.nonUmatGender || "",
      donationAmount: Number(p.donationAmount || 0),
      seqNo: p.seqNo ?? "",
      peserta: typeof p.peserta === "boolean" ? p.peserta : true,
      personType: p.personType || (p.umat ? "Internal" : p.external_person ? "External" : "NonUmat"),
      _lookup: "",
      _lookupExternal: "",
      _isTemp: !!p._meta_temp || (typeof p.id === "string" && p.id?.startsWith?.("tmp")),
      preRegistrationId: p.preRegistrationId || n.documentId || null,
    };
  });

  useEffect(() => {
    const p = participant || {};
    const n = p.non_umat_pre_registration || {};
    setForm({
      ...p,
      umat: { ...(p.umat || {}) },
      external_person: { ...(p.external_person || {}) },
      umats: p.umats ? [...p.umats] : [],
      external_people: p.external_people ? [...p.external_people] : [],
      nonUmatName: n.nonUmatName || "",
      nonUmatPhoneNo: n.nonUmatPhoneNo || "",
      nonUmatRemarks: n.nonUmatRemarks || "",
      nonUmatGender: n.nonUmatGender || "",
      donationAmount: Number(p.donationAmount || 0),
      seqNo: p.seqNo ?? "",
      peserta: typeof p.peserta === "boolean" ? p.peserta : true,
      personType: p.personType || (p.umat ? "Internal" : p.external_person ? "External" : "NonUmat"),
      _lookup: "",
      _lookupExternal: "",
      _isTemp: !!p._meta_temp || (typeof p.id === "string" && p.id?.startsWith?.("tmp")),
    });

    // sync lookup selected state from participant so lookup shows current selection
    if (p) {
      if (p.umats && Array.isArray(p.umats) && p.umats.length) {
        setSelectedUmat(p.umats);
      } else if (p.umat && p.umat.id) {
        setSelectedUmat(p.umat);
      } else {
        setSelectedUmat(null);
      }

      if (p.external_people && Array.isArray(p.external_people) && p.external_people.length) {
        setSelectedExternalPerson(p.external_people);
      } else if (p.external_person && p.external_person.id) {
        setSelectedExternalPerson(p.external_person);
      } else {
        setSelectedExternalPerson(null);
      }
    }

  }, [participant]);

  function update(delta) { setForm(f => ({ ...f, ...delta })); }
  function updateNested(path, deltaObj) { setForm(f => ({ ...f, [path]: { ...(f[path] || {}), ...deltaObj } })); }

  // --- sinkronisasi selectedUmat -> form (replace handleLookupUmatSelect) ---
  useEffect(() => {
    // selectedUmat bisa: null, object (single), or array (multiple)
    setForm(prev => {
      const next = { ...prev };

      // clear previous related fields
      delete next.umat;
      delete next.umats;

      if (!selectedUmat) {
        // nothing selected -> ensure fields cleared
        next.umat = null;
        next.umats = [];
      } else if (Array.isArray(selectedUmat)) {
        // multi-select: set umats as array of objects (keep whole objects)
        next.umats = selectedUmat.map(s => ({ ...(s || {}) }));
        next.umat = null;
      } else {
        // single-select object
        next.umat = { ...(selectedUmat || {}) };
        next.umats = [];
      }

      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUmat]);

  // --- sinkronisasi selectedExternalPerson -> form (replace handleLookupExternalSelect) ---
  useEffect(() => {
    setForm(prev => {
      const next = { ...prev };

      delete next.external_person;
      delete next.external_people;

      if (!selectedExternalPerson) {
        next.external_person = null;
        next.external_people = [];
      } else if (Array.isArray(selectedExternalPerson)) {
        next.external_people = selectedExternalPerson.map(s => ({ ...(s || {}) }));
        next.external_person = null;
      } else {
        next.external_person = { ...(selectedExternalPerson || {}) };
        next.external_people = [];
      }

      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExternalPerson]);


  function applyQuickDonation(amount) { update({ donationAmount: Number(form.donationAmount || 0) + Number(amount || 0) }); }
  function resetDonation() { update({ donationAmount: 0 }); }

  // derived flags
  // batch detection for Internal (umats array length >1)
  const isBatchInternal = form.personType === "Internal" && Array.isArray(form.umats) && form.umats.length > 1;
  const disablePetugasForNonUmat = form.personType === "NonUmat";

  // determine whether seqNo must be governed by first event (oneTimeTagNumber)
  const oneTimeTagNumber = !!selectedAgenda?.oneTimeTagNumber;
  const isFirstEvent = selectedEventId && firstEventId && String(selectedEventId) === String(firstEventId);
  const seqControlledByFirstEvent = oneTimeTagNumber && !isFirstEvent;

  // AUTO-FILL logic:
  // - If seqControlledByFirstEvent === true: fill seqNo from lookupSeqFromFirstEvent(participant)
  // - Else: same as before: if single selection and getNextSeqNo available, fill default
  useEffect(() => {
    if (seqControlledByFirstEvent) {
      // fill seq from first event map using participant identity (if possible)
      const seqFromFirst = lookupSeqFromFirstEvent ? lookupSeqFromFirstEvent(baseParticipant) : null;
      if (seqFromFirst != null) update({ seqNo: seqFromFirst });
      else update({ seqNo: "" });
      return;
    }

    // previous auto-fill behavior for first-event or when oneTimeTagNumber is false
    if (!getNextSeqNo) return;
    if (isEditingExisting || isFromPreReg) return; // don't override editing/pre-reg
    if (form.seqNo && form.seqNo !== "") return; // don't overwrite user-set

    if (form.personType === "Internal") {
      const umatObj = (form.umat && form.umat.id) ? form.umat : ((form.umats && form.umats.length === 1) ? form.umats[0] : null);
      if (umatObj) {
        const g = (umatObj.gender || "").toString().toLowerCase();
        const genderKey = (g.startsWith("m") || g === "male" || g === "l") ? "M" : (g.startsWith("f") || g === "female" || g === "p") ? "F" : null;
        if (genderKey) {
          const next = getNextSeqNo(genderKey);
          if (next != null) update({ seqNo: next });
        }
      }
    } else if (form.personType === "External") {
      const ext = form.external_person;
      if (ext && ext.gender) {
        const g = (ext.gender || "").toString().toLowerCase();
        const genderKey = (g.startsWith("m") || g === "male" || g === "l") ? "M" : (g.startsWith("f") || g === "female" || g === "p") ? "F" : null;
        if (genderKey) {
          const next = getNextSeqNo(genderKey);
          if (next != null) update({ seqNo: next });
        }
      }
    }
    else if (form.personType === "NonUmat") {
      const g = (form.nonUmatGender || "").toString().toLowerCase();

      const genderKey =
        (g.startsWith("m") || g === "male" || g === "l" || g.startsWith("乾")) ? "M" :
        (g.startsWith("f") || g === "female" || g === "p" || g.startsWith("坤")) ? "F" :
        null;

      if (genderKey) {
        const next = getNextSeqNo(genderKey);
        if (next != null) update({ seqNo: next });
      }
    }    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.personType, form.umat, form.umats, form.external_person, getNextSeqNo, isEditingExisting, isFromPreReg, seqControlledByFirstEvent]);

  async function upsertNonUmatPreRegistration(form, selectedAgenda) {
    const API = "/api/agenda-pre-registrations";

    const payload = {
      nonUmatName: form.nonUmatName || "",
      nonUmatPhoneNo: form.nonUmatPhoneNo || "",
      nonUmatRemarks: form.nonUmatRemarks || "",
      nonUmatGender: form.nonUmatGender || "",
      personType: "NonUmat",
      peserta: true,
      agenda: selectedAgenda?.id,
    };

    const user = getUser();
    if (user?.username) {
      payload.recordOwner = user.username;
      payload.recordEditor = user.username;
    }

    // 🔥 UPDATE kalau sudah ada
    if (form.preRegistrationId) {
      const resp = await apiFetch(
        `${API}/${encodeURIComponent(form.preRegistrationId)}`,
        { method: "PUT", body: { data: payload } }
      );

      if (!resp || resp.error) {
        console.error("❌ PRE-REG UPDATE ERROR:", resp);
        throw new Error("Update pre-registration gagal");
      }

      return resp?.data ?? resp;
    }

    // 🆕 CREATE baru
    const resp = await apiFetch(API, {
      method: "POST",
      body: { data: payload }
    });

    if (!resp || resp.error) {
      console.error("❌ PRE-REG CREATE ERROR:", resp);
      throw new Error("Create pre-registration gagal");
    }

    return resp?.data ?? resp;
  }

  async function handleSubmit(e) {
    e && e.preventDefault();

    if (isSubmitting) return; // 🔥 cegah double click
    setIsSubmitting(true);

    // VALIDASI INTERNAL WAJIB PUNYA MINIMAL 1 UMAT
    if (form.personType === "Internal") {

      const hasSingle =
        !!form.umat?.id;

      const hasMultiple =
        Array.isArray(form.umats) &&
        form.umats.length > 0;

      if (!hasSingle && !hasMultiple) {
        alert("Umat belum dipilih");
        setIsSubmitting(false);
        return;
      }
    }

    // enforce NonUmat cannot be petugas
    if (form.personType === "NonUmat") update({ peserta: true });

    // helper endpoints (sesuaikan jika beda)
    const PARTICIPANT_API = "/api/event-participants";

    // helper to build minimal payload for server
    const buildPayload = (o) => {
      const payload = { event: selectedEventId ?? null };
      if (o.personType === "Internal" && o.umat) 
      { 
        //console.log("internal", o.umat)
      }
      if (o.personType === "External" && o.external_person) 
      {
        //console.log("external", o.external_person)
      }
      if (o.personType === "NonUmat" && preRegDoc) {
        payload.non_umat_pre_registration =
          preRegDoc.documentId || preRegDoc.id;
      }
      payload.personType = o.personType;
      payload.peserta = !!o.peserta;
      payload.seqNo = (o.seqNo === "" || o.seqNo == null) ? null : o.seqNo;
      payload.donationAmount = (o.donationAmount == null) ? null : Number(o.donationAmount || 0);
      // optional tracking fields (recordOwner/editor)
      const u = (typeof getUser === "function" ? getUser() : null);
      if (u?.username) { payload.recordOwner = u.username; payload.recordEditor = u.username; }

      // include lookup arrays/objects when present
      if (o.umats && Array.isArray(o.umats) && o.umats.length) {
        // Send minimal structure (ids) or full objects depending server expectation.
        // Strapi v5 often expects relation IDs or objects; here we include IDs array.
        payload.umats = o.umats.map(u => (u?.id ? { id: u.id } : null)).filter(Boolean);
      } else if (o.umat && o.umat.id) {
        payload.umat = { id: o.umat.id };
      }

      if (o.external_people && Array.isArray(o.external_people) && o.external_people.length) {
        payload.external_people = o.external_people.map(e => (e?.id ? { id: e.id } : null)).filter(Boolean);
      } else if (o.external_person && o.external_person.id) {
        payload.external_person = { id: o.external_person.id };
      }

      return payload;
    };

    // BATCH: multiple internal umats -> create many participants
    if (form.personType === "Internal" && Array.isArray(form.umats) && form.umats.length > 1) {
      const outArr = form.umats.map(u => ({
        personType: "Internal",
        umat: { id: u.id, fullName: u.fullName, umatID: u.umatID, avatar: u.avatar, gender: u.gender },
        peserta: !!form.peserta,
        seqNo: null,
        donationAmount: null,
        _meta_batch: true,
      }));
      // send all to server
      try {
        const promises = outArr.map(o => apiFetch(PARTICIPANT_API, { method: "POST", body: { data: buildPayload(o) } }));
        const results = await Promise.all(promises);

        const createdDocs = results.map(r => r?.data ?? r);

        // 🔥 re-fetch populated
        const fullResults = await Promise.all(
          createdDocs.map(c =>
            apiFetch(
              `${PARTICIPANT_API}/${encodeURIComponent(c.documentId || c.id)}?` +
              `populate[event]=true&` +
              `populate[external_person]=true&` +
              `populate[umat][populate][profilepic]=true`
            )
          )
        );

        onSave && onSave(
          fullResults.map(r => r?.data ?? r),
          { mode: "create-batch" }
        );
        
        // return raw server responses to parent
        //onSave && onSave(results.map(r => r?.data ?? r), { mode: "create-batch" });
      } catch (err) {
        console.error("Batch create failed", err);
        throw err;
      }
      return;
    }

    // SINGLE: build out object
    const out = {};
    out.personType = form.personType;
    out.peserta = !!form.peserta;
    const isPetugas = out.peserta === false;

    if (form.personType === "Internal") {
      const umatObj = (form.umat && form.umat.id) ? form.umat : ((form.umats && form.umats.length === 1) ? form.umats[0] : null);
      if (umatObj) out.umat = { ...(umatObj.id ? { id: umatObj.id } : {}), fullName: umatObj.fullName || null, umatID: umatObj.umatID || null, avatar: umatObj.avatar || null, gender: umatObj.gender || null };
    } else if (form.personType === "External") {
      out.external_person = { ...(form.external_person || {}) };
    } else if (form.personType === "NonUmat") {
      out.nonUmatName = form.nonUmatName || "";
      out.nonUmatPhoneNo = form.nonUmatPhoneNo || "";
      out.nonUmatRemarks = form.nonUmatRemarks || "";
    }

    let preRegDoc = null;

    if (form.personType === "NonUmat") {
      preRegDoc = await upsertNonUmatPreRegistration(form, selectedAgenda);

      if (!preRegDoc?.documentId && !preRegDoc?.id) {
        throw new Error("Pre-registration tidak valid");
      }
    }

    // seq rule
    if (seqControlledByFirstEvent) {
      const seqFromFirst = lookupSeqFromFirstEvent ? lookupSeqFromFirstEvent(baseParticipant) : null;
      out.seqNo = seqFromFirst == null ? null : seqFromFirst;
    } else {
      out.seqNo = isPetugas ? null : (form.seqNo === "" ? null : (form.seqNo ?? null));
    }

    out.donationAmount = Number(form.donationAmount || 0);

    // decide mode and perform server calls
    try {
      if (isEditingExisting) {
        // UPDATE existing participant (PUT)
        const docId = baseParticipant.documentId || baseParticipant.id;
        if (!docId) throw new Error("Missing documentId for update");
        const resp = await apiFetch(`${PARTICIPANT_API}/${encodeURIComponent(docId)}`, { method: "PUT", body: { data: buildPayload(out) } });

        if (!resp || resp.error) {
          console.error("❌ API ERROR:", resp);
          throw new Error(resp?.error?.message || "Update failed");
        }

        onSave && onSave(resp?.data ?? resp, { mode: "update" });
        return;
      }
      console.log("📤 PAYLOAD:", buildPayload(out));
      // normal create
      const resp = await apiFetch(PARTICIPANT_API, {
        method: "POST",
        body: { data: buildPayload(out) }
      });

      if (!resp || resp.error) {
        console.error("❌ API ERROR:", resp);
        throw new Error(resp?.error?.message || "Create failed");
      }

      const created = resp?.data ?? resp;
      const docId = created?.documentId || created?.id;

      if (!created?.documentId) {
        throw new Error("Create success but missing documentId");
      }

      // 🟢 Re-fetch dengan populate
      const full = await apiFetch(
//        `${PARTICIPANT_API}/${encodeURIComponent(docId)}?populate=*`
        `${PARTICIPANT_API}/${encodeURIComponent(docId)}?` +
        `populate[event]=true&` +
        `populate[external_person]=true&` +
        `populate[umat][populate][profilepic]=true&` +
        `populate[non_umat_pre_registration]=true`
      );

      if (!full) {
        console.error("❌ Refetch failed, fallback to created:", created);
        onSave && onSave(created, { mode: "create" });
        return;
      }

      onSave && onSave(full?.data ?? full, { mode: "create" });
    } catch (err) {
      console.error("Submit failed", err);
      throw err;
    }
  }


  function handleCancel() { if (typeof onCancel === "function") onCancel(); }

  // UI: personType options follow agenda flags
  const allowExternal = !!selectedAgenda?.allowUmatExternal;
  const allowNonUmat = !!selectedAgenda?.allowPublic;

  const labelStyle = { display: "block", marginBottom: rem(0.18), fontSize: rem(0.9), color: "#234" };
  const inputStyle = { width: "80%", padding: `${rem(0.45)} ${rem(0.6)}`, fontSize: rem(0.95), borderRadius: rem(0.28), border: "1px solid rgba(0,0,0,0.08)" };

  return (
    <form onSubmit={handleSubmit} style={{ fontSize: rem(1), color: "#0b2f4a" }}>
      {!form._isTemp && (
        <div style={{ padding: `${rem(0.2)} 0 ${rem(0.6)} 0` }}>
          <SummaryHeader
            avatar={(form.umat && form.umat.avatar) || (form.external_person && form.external_person.avatar) || getAvatarPlaceholder()}
            umatID={(form.umat && (form.umat.umatID || form.umat.kode)) || ""}
            chineseName={(form.umat && form.umat.chineseName) || ""}
            pinyinName={(form.umat && form.umat.pinyinName) || ""}
            fullName={(form.umat && (form.umat.fullName || form.umat.nickName)) || (form.external_person && form.external_person.name) || (form.nonUmatName || "Participant")}
          />
        </div>
      )}

      <div style={{ display: "grid", gap: rem(0.6) }}>
        <div>
          <label style={labelStyle}>Person Type</label>
          <select
            value={form.personType}
            disabled={isEditingExisting || isFromPreReg}
            onChange={(e) => {
              if (isEditingExisting || isFromPreReg) return;
              const v = e.target.value;
              if (v === "NonUmat") {
                update({ personType: v, external_person: {}, umat: {}, umats: [], nonUmatName: "", nonUmatPhoneNo: "", nonUmatRemarks: "", peserta: true });
              } else if (v === "External") {
                update({ personType: v, umat: {}, umats: [], nonUmatName: "", nonUmatPhoneNo: "", nonUmatRemarks: "" });
              } else {
                update({ personType: v, external_person: {}, nonUmatName: "", nonUmatPhoneNo: "", nonUmatRemarks: "" });
              }
            }}
            style={inputStyle}
          >
            <option value="Internal">Umat Li En</option>
            {allowExternal ? <option value="External">Umat Eksternal</option> : null}
            {allowNonUmat ? <option value="NonUmat">Umum</option> : null}
          </select>
        </div>

        {form.personType === "Internal" && (
          <div>
            {isEditingExisting || isFromPreReg ? (
              <div style={{ padding: rem(0.4), fontWeight: 600 }}>
                {(form.umat && (form.umat.fullName || form.umat.umatID)) || ((form.umats && form.umats.length > 0) ? form.umats.map(u => u.fullName || u.umatID).join(", ") : "-")}
              </div>
            ) : (
              <>
                <LookupUmat
                  multiple={true}
                  value={selectedUmat}
                  onChange={(v) => {
                    if (lockedKodeUmat) return;
                    setSelectedUmat(v);
                  }}
                  initialValue={lockedKodeUmat ? { umatID: lockedKodeUmat } : undefined}
                  disabled={!!lockedKodeUmat}
                  autoFocus={form._isTemp}
                />
              </>
            )}
          </div>
        )}

        {form.personType === "External" && allowExternal && (
          <div>
            {isEditingExisting || isFromPreReg ? (
              <div style={{ padding: rem(0.4), fontWeight: 600 }}>{form.external_person?.name || "-"}</div>
            ) : (
              <LookupExternalPerson
                  multiple={true}
                value={selectedExternalPerson}
                onChange={(v) => {
                  if (lockedKodeExternalPerson) return;
                  setSelectedExternalPerson(v);
                }}
                initialValue={lockedKodeExternalPerson ? { umatID: lockedKodeExternalPerson } : undefined}
                disabled={!!lockedKodeExternalPerson}
                autoFocus={form._isTemp}
              />
            )}
          </div>
        )}

        {form.personType === "NonUmat" && allowNonUmat && (
          <>
            <div>
              <select
                value={form.nonUmatGender || ""}
                onChange={(e) => update({ nonUmatGender: e.target.value })}
                style={inputStyle}
                disabled={isEditingExisting || isFromPreReg}
              >
                <option value="">Gender</option>
                <option value="乾 Pria">乾 Pria</option>
                <option value="坤 Wanita">坤 Wanita</option>
              </select>
            </div>
            <div>
              <input placeholder="Nama peserta" value={form.nonUmatName || ""} onChange={(e) => update({ nonUmatName: e.target.value })} style={inputStyle} disabled={isEditingExisting || isFromPreReg} />
            </div>
            <div>
              <input placeholder="Nomor HP" value={form.nonUmatPhoneNo || ""} onChange={(e) => update({ nonUmatPhoneNo: e.target.value })} style={inputStyle} disabled={isEditingExisting || isFromPreReg} />
            </div>
            <div>
              <input placeholder="Keterangan" value={form.nonUmatRemarks || ""} onChange={(e) => update({ nonUmatRemarks: e.target.value })} style={inputStyle} disabled={isEditingExisting || isFromPreReg} />
            </div>
          </>
        )}

        <div>
          <div style={{ display: "flex", gap: rem(0.8), alignItems: "center" }}>
            <label style={{ fontSize: rem(0.95) }}>
              <input
                type="radio"
                name="kategori"
                checked={!!form.peserta}
                onChange={() => update({ peserta: true })}
                style={{ marginRight: rem(0.35) }}
              />
              Peserta
            </label>
            <label style={{ fontSize: rem(0.95), opacity: disablePetugasForNonUmat ? 0.5 : 1 }}>
              <input
                type="radio"
                name="kategori"
                checked={!form.peserta}
                onChange={() => { if (!disablePetugasForNonUmat) update({ peserta: false }); }}
                style={{ marginRight: rem(0.35) }}
                disabled={disablePetugasForNonUmat}
              />
              Petugas
            </label>
          </div>
        </div>

        {/* seqNo rules:
            - if batch -> hidden
            - if petugas -> hidden
            - if seqControlledByFirstEvent -> show as read-only (value from first event) and not editable
            - else (first event or oneTimeTagNumber false) -> show seq editable & auto-filled via getNextSeqNo
        */}
        {(!isBatchInternal && form.peserta) && (
          <>
            {seqControlledByFirstEvent ? (
              <div>
                <label style={labelStyle}>No. Urut Presensi (ditentukan oleh event pertama)</label>
                <div style={{ padding: rem(0.45), border: "1px solid rgba(0,0,0,0.06)", borderRadius: rem(0.28) }}>
                  <strong>{form.seqNo || "-"}</strong>
                </div>
              </div>
            ) : (
              <div>
                <label style={labelStyle}>號碼 No. Peserta</label>
                <input
                  type="number"
                  value={form.seqNo === null ? "" : (form.seqNo ?? "")}
                  onChange={(e) => update({ seqNo: e.target.value === "" ? "" : Number(e.target.value) })}
                  placeholder="1-99"
                  style={inputStyle}
                  disabled={isEditingExisting && false}
                />
              </div>
            )}
          </>
        )}

        {(!isBatchInternal) && (
          <>
            <div>
              <label style={labelStyle}>助班費 Sumbangan</label>
              <input
                type="number"
                value={form.donationAmount ?? 0}
                onChange={(e) => update({ donationAmount: Number(e.target.value || 0) })}
                style={inputStyle}
                disabled={isEditingExisting && false}
              />
              <div style={{ marginTop: rem(0.45), display: "flex", gap: rem(0.5), flexWrap: "wrap" }}>
                <button type="button" onClick={() => applyQuickDonation(10000)} style={{ background: "#eee", color: "#374151", padding: `${rem(0.35)} ${rem(0.6)}`, borderRadius: rem(0.4), fontSize: rem(0.9) }}>+10rb</button>
                <button type="button" onClick={() => applyQuickDonation(50000)} style={{ background: "#eee", color: "#374151", padding: `${rem(0.35)} ${rem(0.6)}`, borderRadius: rem(0.4), fontSize: rem(0.9) }}>+50rb</button>
                <button type="button" onClick={() => applyQuickDonation(100000)} style={{ background: "#eee", color: "#374151", padding: `${rem(0.35)} ${rem(0.6)}`, borderRadius: rem(0.4), fontSize: rem(0.9) }}>+100rb</button>
                <button type="button" onClick={() => applyQuickDonation(500000)} style={{ background: "#eee", color: "#374151", padding: `${rem(0.35)} ${rem(0.6)}`, borderRadius: rem(0.4), fontSize: rem(0.9) }}>+500rb</button>
                <button type="button" onClick={() => resetDonation()} style={{background: "#eee", color: "#374151", padding: `${rem(0.35)} ${rem(0.6)}`, borderRadius: rem(0.4), fontSize: rem(0.9) }}>Reset</button>
              </div>
            </div>
          </>
        )}

      </div>

      <div style={{ display: "flex", gap: rem(0.6), marginTop: rem(0.9), alignItems: "center" }}>
        <button 
          type="submit"
          disabled={isSubmitting} 
          style={{
            background: isSubmitting ? "#9ca3af" : "#1d4ed8",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            color: "#fff", 
            padding: `${rem(0.6)} ${rem(1.0)}`, 
            borderRadius: rem(0.5), 
            border: "none", 
            fontWeight: 700, 
            fontSize: rem(0.95)
        }}>
          {isSubmitting ? "MENYIMPAN..." : "SIMPAN"}
        </button>
        <button type="button" onClick={handleCancel} style={{
          background: "#fff", color: "#374151", padding: `${rem(0.5)} ${rem(0.9)}`, borderRadius: rem(0.5), border: "1px solid rgba(0,0,0,0.08)", fontSize: rem(0.95)
        }}>
          BATAL
        </button>

        <div style={{ marginLeft: "auto", fontSize: rem(0.85), color: "#6b7280" }}>
          {form._isTemp ? "Baru" : isEditingExisting ? "Edit" : isFromPreReg ? "Pre-Reg" : "Edit"}
        </div>
      </div>
    </form>
  );
}

PresensiCardUmatInfo.propTypes = {
  participant: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  getNextSeqNo: PropTypes.func,
  selectedAgenda: PropTypes.object,
  selectedEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firstEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lookupSeqFromFirstEvent: PropTypes.func,
};
